import { motion } from 'framer-motion';
import { UNIVERSITIES, findUniversityByKey } from '../lib/universities';

export type UniversityVote = { university: string; count: number };

function labelFor(key: string, lang: 'ar' | 'en'): string {
  const u = findUniversityByKey(key);
  if (u) return lang === 'ar' ? u.ar : u.en;
  return key;
}

const TOP_N = 5;

export function PredictionHisto({
  votes,
  totalVotes,
  topByValue,
  topByCount,
  revealMode,
}: {
  votes: UniversityVote[];
  totalVotes: number;
  topByValue: { key: string; en: string; ar: string; value_sar: number; count: number } | null;
  topByCount: { key: string; en: string; ar: string; count: number } | null;
  revealMode: boolean;
}) {
  const visible = votes.slice(0, TOP_N);
  const actualKey = topByValue?.key ?? null;
  if (revealMode && actualKey && !visible.some((v) => v.university === actualKey)) {
    visible.push({ university: actualKey, count: 0 });
  }
  const max = Math.max(1, ...visible.map((v) => v.count));

  return (
    <div className="rounded-2xl bg-white/95 backdrop-blur ring-1 ring-white/30 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.5)] p-5">
      <div className="flex items-baseline justify-between mb-3 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-tatweer-navy/70 font-extrabold">توقّعات الحضور · Audience predictions</div>
          <div className="text-xl font-black text-tatweer-navy mt-1 leading-tight">أيّ جامعة تتصدّر بإجمالي قيمة مناقصاتها على اعتماد؟</div>
          <div className="text-xs text-tatweer-muted mt-1 tabular-nums">
            <span className="font-bold text-tatweer-navy">{totalVotes.toLocaleString()}</span> {totalVotes === 1 ? 'تصويت' : 'تصويت'} · أعلى {Math.min(TOP_N, votes.length)} من ({UNIVERSITIES.length}) جامعة
          </div>
        </div>
        {revealMode && topByValue && (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 220, damping: 22 }}
            className="text-end rounded-2xl bg-gradient-to-br from-tatweer-green/15 to-tatweer-green/5 ring-1 ring-tatweer-green/50 px-6 py-4 shadow-[0_8px_30px_-8px_rgba(45,154,95,0.4)]"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-tatweer-green-600 font-extrabold">الإجابة الفعلية</div>
            <div className="text-base font-extrabold text-tatweer-green-600 mt-1 max-w-[280px] truncate">{topByValue.ar}</div>
            <div className="text-3xl font-black text-tatweer-green-600 tabular-nums leading-tight mt-1.5">
              {(topByValue.value_sar / 1e9).toFixed(2)} <span className="text-sm font-bold">مليار ريال</span>
            </div>
          </motion.div>
        )}
      </div>

      {visible.length === 0 ? (
        <div className="rounded-2xl bg-tatweer-light/80 ring-1 ring-dashed ring-tatweer-line p-12 text-center">
          <div className="text-tatweer-muted text-sm font-medium">لا توجد توقعات بعد — في انتظار أوّل تصويت</div>
          <div className="text-tatweer-muted text-[11px] mt-1">No predictions yet — waiting for the first vote</div>
        </div>
      ) : (
        <ul className="space-y-2" dir="rtl">
          {visible.map((v, idx) => {
            const isWinner = revealMode && actualKey === v.university;
            const pct = (v.count / max) * 100;
            const sharePct = totalVotes > 0 ? Math.round((v.count / totalVotes) * 100) : 0;
            return (
              <motion.li
                key={v.university}
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.25, delay: idx * 0.04 }}
              >
                <div className="flex items-baseline justify-between gap-3 mb-1.5">
                  <span className={`text-sm truncate ${isWinner ? 'text-tatweer-green-600 font-extrabold' : 'text-tatweer-body font-semibold'}`}>
                    {labelFor(v.university, 'ar')}
                    {isWinner && <span className="ms-2 text-[10px] uppercase tracking-wider text-tatweer-green-600 font-extrabold">— الفائز</span>}
                  </span>
                  <span className={`tabular-nums text-xs font-bold flex-shrink-0 ${isWinner ? 'text-tatweer-green-600' : 'text-tatweer-navy/80'}`}>
                    {v.count} {totalVotes > 0 && <span className="opacity-60">({sharePct}%)</span>}
                  </span>
                </div>
                <div className="h-3 rounded-full bg-tatweer-light overflow-hidden ring-1 ring-tatweer-line/50">
                  <motion.div
                    className={`h-full rounded-full ${
                      isWinner
                        ? 'bg-gradient-to-r from-tatweer-green to-emerald-600 shadow-[0_0_12px_rgba(45,154,95,0.5)]'
                        : 'bg-gradient-to-r from-tatweer-navy/80 to-tatweer-navy'
                    }`}
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.max(pct, v.count > 0 ? 4 : 0)}%` }}
                    transition={{ duration: 0.7, ease: 'easeOut', delay: idx * 0.05 }}
                  />
                </div>
              </motion.li>
            );
          })}
        </ul>
      )}

      {revealMode && topByCount && topByValue && topByCount.key !== topByValue.key && (
        <div className="mt-6 pt-4 border-t border-tatweer-line text-xs text-tatweer-muted">
          <span className="font-semibold">للملاحظة:</span> الأعلى عدداً للمناقصات هي{' '}
          <span className="text-tatweer-navy font-bold">{topByCount.ar}</span>
          {' '}بـ{' '}
          <span className="tabular-nums text-tatweer-navy font-bold">{topByCount.count.toLocaleString()}</span> مناقصة (لكنّ القيمة الإجمالية أقل).
        </div>
      )}
    </div>
  );
}
